import React, { useState, useEffect } from "react";
import axios from "axios";
import "../../Styles/AddBillDetails.css";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import Loader from "../../Components/Loader";

function AddBillDetails() {
    const [logo, setLogo] = useState(null);
    const [formData, setFormData] = useState({
        fullName: "",
        subCollegeHeading: "",
        address: "",
        pincode: "",
        phoneNumber: "",
        website: "https://home.uniums.com",
    });
    const [isEditing, setIsEditing] = useState(true);
    const [loading, setLoading] = useState(false);
    const [isDataExists, setIsDataExists] = useState(false);
    const [isFetchingData, setIsFetchingData] = useState(true);

    useEffect(() => {
        fetchCollegeDetails();
    }, []);

    const fetchCollegeDetails = async () => {
        try {
            setIsFetchingData(true);
            const token = localStorage.getItem("authToken");
            const response = await axios.get("https://api.uniums.com/api/bill/college-details", {
                headers: { Authorization: `Bearer ${token}` },
            });

            if (response.status === 200) {
                if (response.data.details) {
                    setFormData(response.data.details);
                    setLogo(response.data.details.logo);
                    setIsEditing(false);
                    setIsDataExists(true);
                    toast.success("College details fetched successfully.");
                } else {
                    setIsEditing(true);
                    setIsDataExists(false);
                    toast.info("No existing college details found. Please add details.");
                }
            }
        } catch (error) {
            console.error("Error fetching college details:", error);
            setIsEditing(true);
            setIsDataExists(false);
            toast.error("Failed to fetch college details. Please try again.");
        } finally {
            setIsFetchingData(false);
        }
    };

    const handleLogoUpload = (event) => {
        const file = event.target.files[0];
        if (file) {
            // Validate file size (e.g., max 5MB)
            if (file.size > 5 * 1024 * 1024) {
                toast.error("Logo file size should not exceed 5MB.");
                return;
            }
            setLogo(file);
        }
    };

    const handleChange = (event) => {
        const { name, value } = event.target;
        setFormData(prevData => ({ ...prevData, [name]: value }));
    };

    const handleSave = async () => {
        // Validate required fields
        const requiredFields = ['fullName', 'subCollegeHeading', 'address', 'pincode', 'phoneNumber'];
        const missingFields = requiredFields.filter(field => !formData[field]);

        if (missingFields.length > 0) {
            toast.error("All fields are required.");
            return;
        }

        // Additional validations
        if (formData.phoneNumber && !/^\d{10}$/.test(formData.phoneNumber)) {
            toast.error("Phone number must be 10 digits.");
            return;
        }

        if (formData.pincode && !/^\d{6}$/.test(formData.pincode)) {
            toast.error("Pincode must be 6 digits.");
            return;
        }

        setLoading(true);
        const token = localStorage.getItem("authToken");
        const formdata = new FormData();

        // Append form data
        Object.keys(formData).forEach(key => {
            formdata.append(key, formData[key]);
        });

        // Append logo if uploaded
        if (logo instanceof File) {
            formdata.append("logo", logo);
        }

        try {
            const response = await axios.post(
                "https://api.uniums.com/api/bill/save-college-details",
                formdata,
                {
                    headers: {
                        Authorization: `Bearer ${token}`,
                        "Content-Type": "multipart/form-data",
                    },
                }
            );

            if (response.status === 201 || response.status === 200) {
                toast.success("College details saved successfully.");
                fetchCollegeDetails(); // Refresh data after saving
                setIsEditing(false);
                setIsDataExists(true);
            }
        } catch (error) {
            console.error("Error saving college details:", error);
            toast.error(error.response?.data?.message || "Failed to save college details");
        } finally {
            setLoading(false);
        }
    };

    // If data is being fetched, show loader
    if (isFetchingData) {
        return <Loader />;
    }

    return (<>
        <div className="add-bill-details-container">
            <h1>College Bill Details</h1>
            <div className="add-bill-details-grid">
                <div className="add-bill-input-group">
                    <label>Full College Name: <span>*</span></label>
                    <input
                        type="text"
                        name="fullName"
                        value={formData.fullName}
                        onChange={handleChange}
                        disabled={!isEditing}
                        required
                    />
                </div>

                <div className="add-bill-input-group">
                    <label>Sub College Heading: <span>*</span></label>
                    <input
                        type="text"
                        name="subCollegeHeading"
                        value={formData.subCollegeHeading}
                        onChange={handleChange}
                        disabled={!isEditing}
                        required
                    />
                </div>

                <div className="add-bill-input-group">
                    <label>Address: <span>*</span></label>
                    <input
                        type="text"
                        name="address"
                        value={formData.address}
                        onChange={handleChange}
                        disabled={!isEditing}
                        required
                    />
                </div>

                <div className="add-bill-input-group">
                    <label>Pincode: <span>*</span></label>
                    <input
                        type="text"
                        name="pincode"
                        value={formData.pincode}
                        onChange={handleChange}
                        disabled={!isEditing}
                        required
                    />
                </div>

                <div className="add-bill-input-group">
                    <label>Phone Number: <span>*</span></label>
                    <input
                        type="text"
                        name="phoneNumber"
                        value={formData.phoneNumber}
                        onChange={handleChange}
                        disabled={!isEditing}
                        required
                    />
                </div>

                <div className="add-bill-input-group">
                    <label>College Website:</label>
                    <input
                        type="text"
                        name="website"
                        value={formData.website}
                        onChange={handleChange}
                        disabled={!isEditing}
                        required
                    />
                </div>

                <div className="add-bill-logo-upload">
                    <label>Upload College Logo: <span>*</span></label>
                    <input
                        type="file"
                        accept="image/*"
                        onChange={handleLogoUpload}
                        disabled={!isEditing}
                    />
                    {logo && (
                        <img
                            src={logo instanceof File ? URL.createObjectURL(logo) : logo}
                            alt="College Logo"
                            className="add-bill-logo-preview"
                        />
                    )}
                </div>
            </div>

            <div className="add-bill-buttons">
                {isEditing ? (
                    <button onClick={handleSave} disabled={loading}>
                        {loading ? "Saving..." : "Save"}
                    </button>
                ) : (
                    <button onClick={() => setIsEditing(true)}>Edit</button>
                )}
                {loading && <Loader />}
            </div>
        </div>
        <ToastContainer />
    </>

    );
}

export default AddBillDetails;